import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { UserRoundX, UserRoundPen } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { UserProfile, StudentProfile } from '../../../types/auth.types';
import { studentsService } from '../../../services/students.service';

const AccountSettingsContent: React.FC = () => {
  const { authStatus, updateUserData } = useAuth();
  const userData = useMemo(
    () => authStatus.metadata || {},
    [authStatus.metadata]
  );
  // Type guard for checking if metadata contains student-specific fields
  const hasStudentData = (
    data: Partial<UserProfile>
  ): data is Partial<StudentProfile> => {
    return data?.userType === 'student';
  };
  // const memoizedUserData = useMemo(() => {
  //   return {
  //     firstName: userData.firstName || '',
  //     lastName: userData.lastName || '',
  //     phone: userData.phone || '',
  //     school: hasStudentData(userData) ? userData.school || '' : '',
  //     grade: hasStudentData(userData) ? userData.grade?.toString() || '' : '',
  //   };
  // }, [userData]);

  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

  // Editable states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [school, setSchool] = useState('');
  const [grade, setGrade] = useState('');
  const [gradeError, setGradeError] = useState('');
  const [phoneticName, setPhoneticName] = useState('');

  const loadUserData = useCallback((userData: Partial<UserProfile>) => {
    setFirstName(userData.firstName || '');
    setLastName(userData.lastName || '');
    setPhone(userData.phone || '');
    setPhoneticName(userData.phoneticName || '');
    if (hasStudentData(userData)) {
      setSchool(userData.school || '');
      setGrade(userData.grade?.toString() || '');
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    if (authStatus.metadata) {
      loadUserData(authStatus.metadata);
    }
  }, [authStatus, loadUserData]);

  // Type guard for checking if metadata contains student-specific fields

  // Format phone number for readability
  const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  const deleteAccount = async (): Promise<void> => {
    setDeletionDialogOpen(false);
    if (authStatus.user) {
      await studentsService.deleteStudentAccount(authStatus.user.uid);
      window.location.reload();
    }
  };

  const openConfirmDeletionDialog = () => {
    setDeletionDialogOpen(true);
  };

  const handleUpdate = async (): Promise<void> => {
    const numericGrade = Number(grade);

    if (isNaN(numericGrade) || numericGrade < 5 || numericGrade > 12) {
      setGradeError('Grade must be a number between 5 and 12');
      return;
    } else {
      setGradeError('');
    }

    if (!authStatus.user) return;

    await updateUserData({
      firstName,
      lastName,
      school,
      grade: numericGrade,
      phone, //  Now phone can also be updated!
      phoneticName: phoneticName || undefined,
    });
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        Account Settings
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={authStatus.user?.email || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formatPhoneNumber(phone)}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          {hasStudentData(userData) && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phonetic Name (optional)"
                  value={phoneticName}
                  onChange={(e) => setPhoneticName(e.target.value)}
                  helperText="Add pronunciation to help announcers read your name correctly"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School"
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  type="number"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  error={!!gradeError}
                  helperText={gradeError}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 2 }}
              startIcon={<UserRoundPen />}
              onClick={handleUpdate}
            >
              Update Account Info
            </Button>
            <Button
              variant="outlined"
              color="error"
              sx={{ mr: 2 }}
              startIcon={<UserRoundX />}
              onClick={openConfirmDeletionDialog}
            >
              Delete Account
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Deletion Dialog */}
      <Dialog
        open={deletionDialogOpen}
        onClose={() => setDeletionDialogOpen(false)}
      >
        <DialogTitle>Delete Account and Data</DialogTitle>
        <DialogContentText sx={{ m: 2 }}>
          <Box>
            <Typography>
              Are you sure you want to delete your account? <br /> This cannot
              be undone and you will lose all projects & paperwork progress.
            </Typography>
          </Box>
        </DialogContentText>
        <DialogActions>
          <Button onClick={() => setDeletionDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteAccount} variant="contained" color="error">
            Confirm Deletion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettingsContent;
